import type { Dayjs } from "dayjs";

import { twMerge } from "tailwind-merge";

import { Button } from "@/components/button";
import { useUserTimezone } from "@/hooks/useUserTimezone";

import type { ScheduledPost } from "./scheduledPost";

import { CalendarEvent } from "./CalendarEvent";
import { inTimeZone, isSameDay } from "./timeZone";

const MAX_VISIBLE_POSTS = 2;

export function MonthView({
  date,
  onSelectPost,
  onShowWeek,
  posts,
  selectedPostId,
}: {
  date: Dayjs;
  onSelectPost: (post: ScheduledPost) => void;
  onShowWeek: (day: Dayjs) => void;
  posts: ScheduledPost[];
  selectedPostId?: number;
}) {
  const firstVisibleDay = date.startOf("month").startOf("week");
  const lastVisibleDay = date.endOf("month").endOf("week");
  const weekCount = lastVisibleDay.diff(firstVisibleDay, "week") + 1;
  const weekStarts = Array.from({ length: weekCount }, (_, week) =>
    firstVisibleDay.add(week, "week"),
  );
  const daysOfWeek = (weekStart: Dayjs) =>
    Array.from({ length: 7 }, (_, day) => weekStart.add(day, "day"));
  const { timezone } = useUserTimezone();
  const today = inTimeZone(timezone);

  return (
    <div className="flex flex-col">
      <div className="grid grid-cols-7 border-b border-border-secondary">
        {daysOfWeek(firstVisibleDay).map((day) => (
          <div
            className="py-md text-center text-sm font-medium text-text-tertiary"
            key={day.toString()}
          >
            {day.format("dd")}
          </div>
        ))}
      </div>

      {weekStarts.map((weekStart) => (
        <div
          className="grid grid-cols-7 border-b border-border-secondary last:border-b-0"
          key={weekStart.toString()}
        >
          {daysOfWeek(weekStart).map((day) => {
            const dayPosts = posts.filter((post) =>
              isSameDay(inTimeZone(timezone, post.scheduledAt), day),
            );
            const hiddenCount = dayPosts.length - MAX_VISIBLE_POSTS;

            return (
              <div
                className={twMerge(
                  "flex min-h-[120px] min-w-0 flex-col gap-xs border-l border-border-secondary p-xs first:border-l-0",
                  isSameDay(day, today) && "bg-bg-brand-primary/40",
                )}
                key={day.toString()}
              >
                <span
                  className={twMerge(
                    "self-start rounded-full px-sm text-sm font-medium text-text-secondary",
                    !day.isSame(date, "month") && "text-text-quaternary",
                    isSameDay(day, today) &&
                      "bg-bg-brand-solid text-text-white",
                  )}
                >
                  {day.date()}
                </span>
                {dayPosts.slice(0, MAX_VISIBLE_POSTS).map((post) => (
                  <CalendarEvent
                    isSelected={post.id === selectedPostId}
                    key={post.id}
                    onPress={() => onSelectPost(post)}
                    post={post}
                  />
                ))}
                {hiddenCount > 0 && (
                  <Button
                    className="self-start"
                    onPress={() => onShowWeek(day)}
                    size="sm"
                    variant="tertiary"
                  >
                    +{hiddenCount} more
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
